import { parseDisplayIdFromName } from './panelEntityNumbering.js';

export const PANEL_MOVE_NUMBERING_MODES = Object.freeze({
  PRESERVE: 'preserve',
  APPEND_RENUMBER: 'append-renumber'
});

const ENTITY_CONFIG = {
  string: {
    displayKeys: ['displayNo', 'stringId', 'solarStringId'],
    seqKeys: ['seq', 'solarStringSeq'],
    idPrefix: 'string_',
    displayLabel: 'String'
  },
  module: {
    displayKeys: ['displayNo', 'moduleId', 'solarModuleId'],
    seqKeys: ['seq', 'solarModuleSeq'],
    idPrefix: 'module_',
    displayLabel: 'Module'
  }
};

function getEntityConfig(entityType) {
  const config = ENTITY_CONFIG[entityType];
  if (!config) {
    throw new Error(`Unsupported panel move entity type: ${entityType}`);
  }
  return config;
}

function readNumericDisplayNo(item, config) {
  for (const key of config.displayKeys) {
    const raw = item?.[key];
    if (raw == null || raw === '') {
      continue;
    }
    const parsed = Number(raw);
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }
  }

  const fromName = parseDisplayIdFromName(item?.name);
  return Number.isInteger(fromName) && fromName > 0 ? fromName : null;
}

function readEntitySeq(item, config) {
  for (const key of config.seqKeys) {
    if (item?.[key] != null && item[key] !== '') {
      return item[key];
    }
  }

  const rawId = item?.id;
  if (rawId == null || rawId === '') {
    return null;
  }

  const raw = String(rawId);
  const prefix = config.idPrefix;
  const suffix = raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
  const numeric = Number(suffix);
  return Number.isFinite(numeric) ? numeric : suffix;
}

function displayKey(displayNo) {
  return displayNo == null ? null : String(displayNo);
}

function maxDisplayNo(items, config, excludedSeqs) {
  let max = 0;
  for (const item of items || []) {
    const seq = readEntitySeq(item, config);
    if (seq != null && excludedSeqs.has(String(seq))) {
      continue;
    }
    const displayNo = readNumericDisplayNo(item, config);
    if (displayNo != null && displayNo > max) {
      max = displayNo;
    }
  }
  return max;
}

export function buildPanelMoveNumberingPlan({
  entityType,
  movingItems = [],
  targetItems = [],
  mode = PANEL_MOVE_NUMBERING_MODES.PRESERVE
} = {}) {
  const config = getEntityConfig(entityType);
  if (
    mode !== PANEL_MOVE_NUMBERING_MODES.PRESERVE &&
    mode !== PANEL_MOVE_NUMBERING_MODES.APPEND_RENUMBER
  ) {
    throw new Error(`Unsupported panel move numbering mode: ${mode}`);
  }

  const movingSeqs = new Set(
    (movingItems || [])
      .map(item => readEntitySeq(item, config))
      .filter(seq => seq != null)
      .map(seq => String(seq))
  );
  const targetMaxDisplayNo = maxDisplayNo(targetItems, config, movingSeqs);
  const usedDisplayNos = new Set();
  for (const item of targetItems || []) {
    const seq = readEntitySeq(item, config);
    if (seq != null && movingSeqs.has(String(seq))) {
      continue;
    }
    const displayNo = readNumericDisplayNo(item, config);
    if (displayNo != null) {
      usedDisplayNos.add(displayKey(displayNo));
    }
  }

  const pendingAssignments = (movingItems || []).map(item => {
    const previousNumericDisplayNo = readNumericDisplayNo(item, config);
    const previousDisplayNo = displayKey(previousNumericDisplayNo);
    const hadConflict = previousDisplayNo != null && usedDisplayNos.has(previousDisplayNo);
    const canPreserve =
      mode === PANEL_MOVE_NUMBERING_MODES.PRESERVE && previousDisplayNo != null && !hadConflict;

    if (canPreserve) {
      usedDisplayNos.add(previousDisplayNo);
    }

    return {
      entitySeq: readEntitySeq(item, config),
      previousDisplayNo,
      preservedDisplayNo: canPreserve ? previousDisplayNo : null,
      conflictResolved: hadConflict
    };
  });

  let nextDisplayNo = targetMaxDisplayNo + 1;
  const assignments = pendingAssignments.map(pending => {
    let displayNo = pending.preservedDisplayNo;
    if (displayNo == null) {
      while (usedDisplayNos.has(displayKey(nextDisplayNo))) {
        nextDisplayNo += 1;
      }
      displayNo = displayKey(nextDisplayNo);
      usedDisplayNos.add(displayNo);
      nextDisplayNo += 1;
    }

    return {
      entitySeq: pending.entitySeq,
      previousDisplayNo: pending.previousDisplayNo,
      displayNo,
      changed: pending.previousDisplayNo !== displayNo,
      conflictResolved: pending.conflictResolved
    };
  });

  return {
    entityType,
    mode,
    targetMaxDisplayNo,
    assignments
  };
}

export function resolvePanelMoveDisplayName(entityType, currentName, displayNo) {
  const config = getEntityConfig(entityType);
  const normalizedName = String(currentName || '').trim();
  const generatedNamePattern = new RegExp(`^${config.displayLabel}\\s+\\d+$`);

  if (!normalizedName || generatedNamePattern.test(normalizedName)) {
    return `${config.displayLabel} ${displayNo}`;
  }
  return normalizedName;
}
